import React, { useEffect, useRef, useState } from 'react';
import { testimonials } from '../mock';
import { Quote } from 'lucide-react';

const Testimonials = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards(prev => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-32 bg-[#fafafa] relative overflow-hidden">
      {/* 3D Background shapes */}
      <div className="absolute top-1/4 left-10 w-40 h-40 border-2 border-black/5 transform -rotate-45" style={{
        animation: 'float-slow 10s ease-in-out infinite'
      }} />
      <div className="absolute bottom-1/4 right-10 w-32 h-32 border border-black/5 transform rotate-12" style={{
        animation: 'float-slow 12s ease-in-out infinite 2s'
      }} />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="mb-20">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-black" style={{
            textShadow: '2px 2px 0px rgba(0,0,0,0.03)'
          }}>
            What Clients Say
          </h2>
        </div>

        {/* 3D Testimonials grid */}
        <div className="grid md:grid-cols-3 gap-8" style={{ perspective: '2000px' }}>
          {testimonials.map((testimonial, index) => {
            const isVisible = visibleCards.includes(index);
            
            return (
              <div
                key={testimonial.id}
                ref={el => cardsRef.current[index] = el}
                className="relative bg-white border border-black/10 p-8 cursor-pointer group"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: isVisible 
                    ? 'perspective(1000px) rotateY(0deg) translateZ(0px)' 
                    : 'perspective(1000px) rotateY(-20deg) translateZ(-50px)',
                  opacity: isVisible ? 1 : 0,
                  transition: `all 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.15}s`,
                  boxShadow: '15px 15px 40px rgba(0,0,0,0.08)'
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const centerX = rect.width / 2;
                  const centerY = rect.height / 2;
                  const rotateX = (y - centerY) / 20;
                  const rotateY = (centerX - x) / 20;
                  e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(20px)`;
                  e.currentTarget.style.boxShadow = '20px 20px 50px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
                  e.currentTarget.style.boxShadow = '15px 15px 40px rgba(0,0,0,0.08)';
                }}
              >
                {/* Quote icon with 3D depth */}
                <div className="mb-6" style={{ transform: 'translateZ(15px)' }}>
                  <Quote className="w-8 h-8 text-black" />
                </div>

                {/* Content */}
                <p className="text-[#666666] leading-relaxed mb-8 font-light" style={{
                  transform: 'translateZ(10px)'
                }}>
                  {testimonial.content}
                </p>

                {/* Author */}
                <div className="flex items-center gap-4 pt-6 border-t border-black/10" style={{
                  transform: 'translateZ(5px)'
                }}>
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full grayscale group-hover:grayscale-0 transition-all duration-300"
                    style={{
                      boxShadow: '3px 3px 10px rgba(0,0,0,0.2)'
                    }}
                  />
                  <div>
                    <div className="font-semibold text-black">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-[#666666]">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-30px); }
        }
      `}</style>
    </section>
  );
};

export default Testimonials;
